import { z } from 'zod';
import { uuidSchema } from './validation.js';
import { clientPriceAmountSchema } from './client-prices.js';
import { costCurrencySchema } from './cost-currency.js';
import { CODE_TABLE_MAX_ENTRIES, DEFAULT_COLOURLESS_CODE } from '../data/product-sync.js';
/**
 * Base garments (BLU-1394, Products > Base garments): a 3PL's blank garments,
 * one row per product code. Inputs and the RPC read-models only: the
 * organisation and the user always come from auth, so no input schema here
 * carries an organizationId (zod's default strip drops one).
 *
 * The stock type (audience; "Who can use it" until it was named) lives on the
 * code: `own` (Own stock, not offered to clients), `all_clients` (Shared, the
 * backup pool) or `client` (Labelled for one connected client, which is then
 * required, and only then).
 */
export const BASE_GARMENT_AUDIENCES = ['own', 'all_clients', 'client'];
export const baseGarmentAudienceSchema = z.enum(BASE_GARMENT_AUDIENCES);
export const BASE_GARMENT_PRINT_METHODS = ['DTG', 'DTF'];
export const baseGarmentPrintMethodSchema = z.enum(BASE_GARMENT_PRINT_METHODS);
/**
 * The refusal keys of the base_garment_* RPCs, the one list the backend
 * (`lib/base-garment-errors.ts`) and the browser both read. Each rides as
 * `BASE_GARMENT_<KEY>` in the backend's `FORWARDABLE_CAUSE_CODES` (`trpc.ts`),
 * which keeps its literal list; a backend test pins every key into it.
 */
export const BASE_GARMENT_ERROR_KEYS = [
    'CODE_TAKEN',
    'CODE_IN_USE',
    'CLIENT_CONFLICT',
    'CODE_NAME_TAKEN',
    'CLIENT_NOT_CONNECTED',
    'SHAPE_MIX',
    'SKU_NOT_EDITABLE',
    'RESERVED_RENAME',
    'CODE_NOT_FOUND',
    'UNKNOWN_CODE',
    'CODE_TABLE_FULL',
    'IMPORT_INCONSISTENT',
    'TOO_MANY',
    'RELINK_REFUSED',
    // BLU-1527 S6: the Substitute garment editor (detail.reason says why).
    'OWN_SUBSTITUTE',
];
/**
 * A product code the per-org SKU rules can link: a letter first, then up to 9
 * capital letters or digits (the rule the link functions check in SQL).
 */
export const BASE_GARMENT_LINKABLE_CODE_RE = /^[A-Z][A-Z0-9]{0,9}$/;
/** The longest product code: 10 characters. Size and colour codes stay at 6. */
export const BASE_GARMENT_PRODUCT_CODE_MAX = 10;
/** A NEW product code: a letter first, 1 to 10 capital letters or digits. */
export const NEW_PRODUCT_CODE_RE = /^[A-Z][A-Z0-9]{0,9}$/;
/** A NEW size or colour code: 1 to 4 capital letters or digits. */
export const NEW_SEGMENT_CODE_RE = /^[A-Z0-9]{1,4}$/;
/**
 * Any product code already in use, 1 to 10 capital letters or digits (legacy
 * tolerant: it may start with a digit). LOCKSTEP: base_garment_codes_code_check.
 */
export const BASE_GARMENT_CODE_RE = /^[A-Z0-9]{1,10}$/;
/** Any size or colour code already in use: 1 to 6 capital letters or digits. */
export const BASE_GARMENT_SIZE_COLOUR_CODE_RE = /^[A-Z0-9]{1,6}$/;
/** Image addresses typed or uploaded in a CSV: https only, no whitespace. */
export const BASE_GARMENT_IMAGE_URL_RE = /^https:\/\/\S+$/;
/** The grid's image input: the shape only; `isAcceptedBaseGarmentImageUrl` is the rule. */
export const BASE_GARMENT_GRID_IMAGE_URL_RE = /^https?:\/\/\S+$/;
/** Where Supabase serves public storage objects, under the project URL. */
const PUBLIC_STORAGE_PATH = '/storage/v1/object/public/';
/**
 * May the grid store this image address? An https address always. A plain
 * http address only when it is the app's OWN storage (the address our own
 * image upload just returned, which is http on the local rig): it must start
 * with `<ownStorageOrigin>/storage/v1/object/public/`. Typed and CSV addresses
 * stay https only (`BASE_GARMENT_IMAGE_URL_RE`). The browser passes its
 * Supabase URL and the backend its own, so the two always agree; in production
 * that URL is https, so this adds nothing there.
 */
export function isAcceptedBaseGarmentImageUrl(url, ownStorageOrigin) {
    if (BASE_GARMENT_IMAGE_URL_RE.test(url))
        return true;
    if (!ownStorageOrigin || !BASE_GARMENT_GRID_IMAGE_URL_RE.test(url))
        return false;
    const origin = ownStorageOrigin.trim().replace(/\/+$/, '');
    return origin.startsWith('http://') && url.startsWith(`${origin}${PUBLIC_STORAGE_PATH}`);
}
/** A SKU segment as the grid writes it (never contains the `-` separator). */
export const BASE_GARMENT_SEGMENT_RE = /^[^-\s]{1,20}$/;
export const BASE_GARMENT_NAME_MAX = 120;
export const BASE_GARMENT_SEGMENT_NAME_MAX = 64;
/**
 * The most entries one code table holds: an alias of `CODE_TABLE_MAX_ENTRIES`
 * (the normalizer's cap), so the two can never disagree. LOCKSTEP:
 * product_sync_append_codes and base_garment_adopt_codes (54000 / table_full).
 */
export const BASE_GARMENT_CODE_TABLE_MAX = CODE_TABLE_MAX_ENTRIES;
export const BASE_GARMENT_HS_CODE_MAX = 20;
export const BASE_GARMENT_NOTE_MAX = 200;
export const BASE_GARMENT_WEIGHT_MAX_GRAMS = 30000;
// LOCKSTEP: the caps in supabase/migrations/20261013200100_blu1394_base_garment_rpcs.sql.
// BASE_GARMENT_MAX_VARIANTS (SKUs per add) is 1000 since
// 20261030010000_catalog_add_cap_1000.sql (base_garment_add_variants and
// catalog_add_to_location, through CATALOG_MAX_CELLS).
export const BASE_GARMENT_MAX_VARIANTS = 1000;
export const BASE_GARMENT_MAX_GRID_ENTRIES = 500;
export const BASE_GARMENT_MAX_STOCK_LINES = 2000;
export const BASE_GARMENT_MAX_IMPORT_ROWS = 5000;
export const BASE_GARMENT_MAX_IMPORT_NEW_CODES = 500;
export const BASE_GARMENT_STOCK_MAX = 9_999_999;
export const BASE_GARMENT_PAGE_SIZES = [25, 50, 100];
/** Why stock was changed; the English label is written into the stock history. */
export const BASE_GARMENT_STOCK_REASONS = ['stock_count', 'received_no_po', 'damaged', 'found', 'other'];
export const baseGarmentStockReasonSchema = z.enum(BASE_GARMENT_STOCK_REASONS);
export const BASE_GARMENT_STOCK_REASON_LABELS = {
    stock_count: 'Stock count',
    received_no_po: 'Received without a PO',
    damaged: 'Damaged',
    found: 'Found',
    other: 'Other',
};
export const baseGarmentCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(BASE_GARMENT_CODE_RE, 'Use 1 to 10 capital letters or digits');
/** A size or colour code already in use (product codes allow 10; these stay at 6). */
export const sizeColourCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(BASE_GARMENT_SIZE_COLOUR_CODE_RE, 'Use 1 to 6 capital letters or digits');
/** A product code being created or renamed to. */
export const newProductCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(NEW_PRODUCT_CODE_RE, 'Start with a letter, then use up to 9 capital letters or digits');
const newSegmentCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(NEW_SEGMENT_CODE_RE, 'Use 1 to 4 capital letters or digits');
const segmentSchema = z.string().trim().regex(BASE_GARMENT_SEGMENT_RE, 'Not a valid size or colour code');
const nullableSegmentSchema = segmentSchema.nullable();
export const baseGarmentNameSchema = z
    .string()
    .trim()
    .min(1, 'Give the product a name')
    .max(BASE_GARMENT_NAME_MAX, `Use ${BASE_GARMENT_NAME_MAX} characters or fewer`);
const segmentNameSchema = z.string().trim().min(1).max(BASE_GARMENT_SEGMENT_NAME_MAX);
/**
 * The client is required exactly when the audience is `client`. `field` names
 * the path an issue lands on.
 */
export const refineAudienceClient = (value, context, path = [], messages = {
    missing: 'Choose the client this product is for',
    extra: 'A client only goes with Labelled for a client',
}) => {
    const hasClient = value.clientOrganizationId !== undefined && value.clientOrganizationId !== null;
    if (value.audience === 'client' && !hasClient) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: messages.missing,
            path: [...path, 'clientOrganizationId'],
        });
    }
    if (value.audience !== 'client' && hasClient) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: messages.extra,
            path: [...path, 'clientOrganizationId'],
        });
    }
};
// ── Read models (what the RPCs return; parsed fail-closed by the service) ──────────
export const baseGarmentShapeWrongReasonSchema = z.enum([
    'not_pod',
    'locked',
    'shared_mismatch',
    'foreign_mapping',
    'foreign_election',
]);
/** The compact per-code facts of `get_base_garment_list` (EVERY code, never filtered). */
export const baseGarmentFactsSchema = z.object({
    code: z.string(),
    hasRow: z.boolean(),
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: z.string().nullable(),
    variantCount: z.number().int().min(0),
    sizeSegs: z.array(z.string()),
    shapes: z.array(z.number().int()),
    missingInventoryRow: z.number().int().min(0),
    linked: z.boolean(),
    linkedKeep: z.number().int().nullable().optional(),
    sizesWithoutWeight: z.array(z.string().nullable()),
    hasDefaultWeight: z.boolean(),
    costMissing: z.boolean(),
    shapeWrong: z.array(z.object({ sku: z.string(), reason: baseGarmentShapeWrongReasonSchema })),
    shapeWrongCount: z.number().int().min(0),
    noConsent: z.boolean(),
    /**
     * Whose link covers the code: 'global' (a shared list names it), 'org' (the org's own
     * active rule), null when not linked. Re-link applies to 'org' links only. Optional so
     * an older list RPC still parses.
     */
    linkSource: z.enum(['org', 'global']).nullable().optional(),
    /**
     * One real SKU of the code, for the Link action (every code, on the page or not): the
     * byte-first blank whose text is already upper-case and trimmed, else the byte-first
     * blank; null when the code has no blank SKUs. Optional so an older list RPC still parses.
     */
    sampleSku: z.string().nullable().optional(),
    /**
     * Does any stock row of the code's blanks, at ANY location, hold a non-zero count
     * (negative included)? The exact test behind the server's rename / reserve refusal
     * (P0942 stock), so it never depends on the selected location. Only on the page's
     * codes (absent for a code off the page); the service refuses a page code without it.
     */
    hasStockAnywhere: z.boolean().optional(),
    /**
     * A code made for one client (audience `client`): how many of its live blanks no
     * mapping of THAT client points at (its back-up links, keyed by the blank's id). 0 for
     * own and all-clients codes. The client's orders reach these blanks through those
     * links, never through the 3PL's code tables, so a client code is judged by this
     * instead. Optional so an older list RPC still parses (then the old rules apply).
     */
    clientUnlinked: z.number().int().min(0).optional(),
    /**
     * A code made for one client (audience `client`): true when that client has no ACTIVE
     * junction (location_client_organizations) at the viewed location, or with no location
     * at any location the org owns. Blank supply consent does not matter (that is
     * `noConsent`, which folds both). false for own and all-clients codes. Optional so an
     * older list RPC still parses.
     */
    clientNotConnected: z.boolean().optional(),
});
/**
 * What `ensure_org_sku_prefix_rules` can say about one product code. `would_link`
 * is a dry run's "safe to link"; `retired` is a link someone turned off on purpose
 * (only a person's Link turns it back on).
 */
export const SKU_PREFIX_LINK_STATUSES = [
    'linked',
    'would_link',
    'already_linked',
    'conflict',
    'covered_global',
    'finished_goods',
    'needs_review',
    'mixed_shape',
    'retired',
    'skipped',
];
export const skuPrefixLinkStatusSchema = z.enum(SKU_PREFIX_LINK_STATUSES);
/** One code's dry-run answer: why it is not linked, and the SKU behind a review. */
export const baseGarmentLinkStatusSchema = z.object({
    code: z.string(),
    sku: z.string(),
    status: skuPrefixLinkStatusSchema,
    offendingSku: z.string().nullable(),
});
export const baseGarmentWarningsSchema = z.object({
    noPrintSizeRule: z.boolean(),
    missingImage: z.boolean(),
    noLowStockAlert: z.boolean(),
    unpricedClient: z.boolean(),
    sizeLessWithPreprint: z.boolean(),
});
export const baseGarmentCellSchema = z.object({
    id: z.string(),
    sku: z.string(),
    sizeSeg: z.string().nullable(),
    colourSeg: z.string(),
    stock: z.number().nullable(),
    hasInventoryRow: z.boolean(),
    weightValue: z.number().nullable(),
    weightUnit: z.string().nullable(),
    notify: z.number().nullable(),
    /**
     * BLU-1268: the cell's supply mode at the listed location (null: no row there,
     * read as stocked). A just_in_time cell has no stock number. Optional: an older
     * function omits all four.
     */
    stockMode: z.enum(['stocked', 'just_in_time', 'managed_elsewhere']).nullable().optional(),
    storeFixedQty: z.number().nullable().optional(),
    keepAtLeast: z.number().nullable().optional(),
    /** The platform catalog variant the blank was added from, or null. */
    catalogVariantId: z.string().nullable().optional(),
    /** Only with the cost permission. */
    cost: z.number().nullable().optional(),
    costCurrency: z.string().nullable().optional(),
    wac: z.number().nullable().optional(),
});
export const baseGarmentListRowSchema = z.object({
    code: z.string(),
    hasRow: z.boolean(),
    name: z.string(),
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: z.string().nullable(),
    clientName: z.string().nullable(),
    defaultPrintMethod: baseGarmentPrintMethodSchema,
    lowStockAlert: z.number().nullable(),
    supplierId: z.string().nullable(),
    hsCode: z.string().nullable(),
    totalStock: z.number(),
    colours: z.array(z.object({ seg: z.string(), name: z.string().nullable(), image: z.string().nullable() })),
    sizes: z.array(z.object({ seg: z.string(), name: z.string().nullable() })),
    cells: z.array(baseGarmentCellSchema),
    warnings: baseGarmentWarningsSchema,
});
/** `get_base_garment_list` as returned. */
export const baseGarmentListRpcSchema = z.object({
    total: z.number().int().min(0),
    rows: z.array(baseGarmentListRowSchema),
    facts: z.array(baseGarmentFactsSchema),
    codeTablesRaw: z.unknown().nullable().optional(),
    orgCurrency: z.string().nullable().optional(),
    /**
     * BLU-1434: code rows the list leaves out (every SKU PAP or archived). The
     * export only: they join its knownCodes so an upload does not read them as
     * new. Never shown on the list. Optional: an older function omits it.
     */
    hiddenCodes: z.array(z.string()).optional(),
});
// ── Inputs ───────────────────────────────────────────────────────────────────
const searchSchema = z.string().trim().max(64);
/**
 * The stock type filter shared by the Base garments list, Products and
 * Inventory: no audience = Any; a client goes only with `client`.
 */
export const baseGarmentAudienceFilterFields = {
    audience: baseGarmentAudienceSchema.optional(),
    clientOrganizationId: uuidSchema.optional(),
};
export const refineBaseGarmentAudienceFilter = (value, context) => {
    if (value.clientOrganizationId !== undefined && value.audience !== 'client') {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A client filter goes with Only one client',
            path: ['clientOrganizationId'],
        });
    }
};
export const baseGarmentListSchema = z
    .object({
    /** null = no location chosen: the stock columns and stock editing stay hidden. */
    locationId: uuidSchema.nullable(),
    search: searchSchema.optional(),
    ...baseGarmentAudienceFilterFields,
    /** A code to pin first (after create, copy or a deep link). */
    focusCode: baseGarmentCodeSchema.optional(),
    page: z.number().int().min(1).default(1),
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]).default(50),
})
    .superRefine(refineBaseGarmentAudienceFilter);
/** The most SKUs one link-status read takes (it rides in a URL; the browser asks in slices). */
export const BASE_GARMENT_LINK_STATUS_MAX_SKUS = 40;
/** One SKU per code: the dry run judges the whole code from any one of its blanks. */
export const baseGarmentLinkStatusInputSchema = z.object({
    skus: z.array(z.string().trim().min(1).max(100)).min(1).max(BASE_GARMENT_LINK_STATUS_MAX_SKUS),
});
/** Re-link one code's own link to the shape of its blanks. */
export const relinkBaseGarmentCodeSchema = z.object({
    code: z.string().trim().toUpperCase().regex(BASE_GARMENT_LINKABLE_CODE_RE, 'Not a product code'),
});
/** The most product codes one "Add these codes" call names (the dry run rides in a URL). */
export const BASE_GARMENT_ADOPT_MAX_CODES = 40;
/**
 * BLU-1404 "Add these codes": add the product type, size and colour codes the
 * org's own blanks already use to its code tables, named from those SKUs.
 * `codes` names the product codes to read; omitted = every blank of the org.
 * The dry run (adoptCodesPreview) and the write (adoptCodes) take the same input.
 */
export const adoptBaseGarmentCodesSchema = z.object({
    codes: z.array(baseGarmentCodeSchema).min(1).max(BASE_GARMENT_ADOPT_MAX_CODES).optional(),
});
/** Why "Add these codes" left a code out (LOCKSTEP: base_garment_adopt_codes in 20261014100000). */
export const BASE_GARMENT_ADOPT_SKIP_REASONS = ['not_a_code', 'name_taken', 'table_full'];
/**
 * BLU-1435: the size and colour codes a CSV upload adds to the code tables before
 * it saves, each with the name it goes in under. Per kind: at most
 * `BASE_GARMENT_CODE_TABLE_MAX`, each code
 * and each name (any case) once. Colours never take the default colourless code:
 * it means no colour.
 */
const segmentCodeEntriesSchema = (kind) => z
    .array(z.object({ code: sizeColourCodeSchema, name: segmentNameSchema }))
    .max(BASE_GARMENT_CODE_TABLE_MAX)
    .default([])
    .superRefine((entries, context) => {
    const codes = entries.map((entry) => entry.code);
    const names = entries.map((entry) => entry.name.toLowerCase());
    if (new Set(codes).size !== codes.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'A code is listed twice' });
    }
    if (new Set(names).size !== names.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Two codes have the same name' });
    }
    if (kind === 'colours' && codes.includes(DEFAULT_COLOURLESS_CODE)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: `${DEFAULT_COLOURLESS_CODE} means no colour` });
    }
});
export const addBaseGarmentSizeColourCodesSchema = z
    .object({
    sizes: segmentCodeEntriesSchema('sizes'),
    colours: segmentCodeEntriesSchema('colours'),
})
    .superRefine((value, context) => {
    if (value.sizes.length + value.colours.length === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'There is nothing to add', path: ['sizes'] });
    }
});
/**
 * BLU-1442 the Codes panel on Base garments: the three code tables it edits, in
 * tab order. LOCKSTEP: product_sync_rename_code / product_sync_remove_code
 * (20261016150000) take the same kinds.
 */
export const BASE_GARMENT_CODE_TABLE_KINDS = ['colours', 'sizes', 'productTypes'];
export const baseGarmentCodeTableKindSchema = z.enum(BASE_GARMENT_CODE_TABLE_KINDS);
/**
 * One code-table entry as the panel shows it: its store name and the code it
 * maps. The SQL finds the entry by name and refuses (P0002 entry_changed)
 * when it no longer maps this code.
 */
const codeTableEntryFields = {
    kind: baseGarmentCodeTableKindSchema,
    name: segmentNameSchema,
    code: z.string().trim().toUpperCase(),
};
const refineCodeTableEntryCode = (value, context) => {
    const re = value.kind === 'productTypes' ? BASE_GARMENT_CODE_RE : BASE_GARMENT_SIZE_COLOUR_CODE_RE;
    if (!re.test(value.code)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: value.kind === 'productTypes' ? 'Use 1 to 10 capital letters or digits' : 'Use 1 to 6 capital letters or digits',
            path: ['code'],
        });
    }
};
/** Rename ONE store name of a code table. The code never changes. */
export const renameBaseGarmentCodeEntrySchema = z
    .object({ ...codeTableEntryFields, newName: segmentNameSchema })
    .superRefine(refineCodeTableEntryCode);
/** Delete ONE store name of a code table (the code goes with its last name, only when nothing uses it). */
export const removeBaseGarmentCodeEntrySchema = z.object(codeTableEntryFields).superRefine(refineCodeTableEntryCode);
/**
 * BLU-1442 the starter colours offered to a 3PL whose colour table is empty, in
 * this order. Added through addSizeColourCodes; the names stay English (they are
 * the store values product sync matches).
 */
export const STARTER_COLOUR_CODES = [
    { code: 'BK', name: 'Black' },
    { code: 'WH', name: 'White' },
    { code: 'NT', name: 'Natural' },
    { code: 'NY', name: 'Navy' },
    { code: 'GY', name: 'Grey' },
    { code: 'CR', name: 'Charcoal' },
    { code: 'RD', name: 'Red' },
    { code: 'PK', name: 'Pink' },
    { code: 'BW', name: 'Brown' },
    { code: 'GN', name: 'Green' },
    { code: 'BE', name: 'Blue' },
    { code: 'YL', name: 'Yellow' },
];
export const baseGarmentExportSchema = z.object({
    locationId: uuidSchema,
    kind: z.enum(['template', 'all']),
});
export const createBaseGarmentProductSchema = z
    .object({
    code: newProductCodeSchema,
    name: baseGarmentNameSchema,
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: uuidSchema.optional(),
})
    .superRefine((value, context) => refineAudienceClient(value, context));
/** One axis (sizes or colours) of an Add variants call. */
export const baseGarmentAxisSchema = z
    .object({
    /** No size / No colour: the axis contributes no segment (colours use the colourless code). */
    none: z.boolean().default(false),
    /** Codes already in the org's code tables, or already on the code. */
    codes: z.array(sizeColourCodeSchema).max(BASE_GARMENT_MAX_VARIANTS).default([]),
    /** New codes, written to the org's code tables in the same transaction. */
    newCodes: z
        .array(z.object({ name: segmentNameSchema, code: newSegmentCodeSchema }))
        .max(BASE_GARMENT_MAX_VARIANTS)
        .default([]),
})
    .superRefine((value, context) => {
    const picked = value.codes.length + value.newCodes.length;
    if (value.none && picked > 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose No size/colour or codes, not both', path: ['none'] });
    }
    if (!value.none && picked === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose at least one', path: ['codes'] });
    }
    const all = [...value.codes, ...value.newCodes.map((n) => n.code)];
    if (new Set(all).size !== all.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'A code is listed twice', path: ['codes'] });
    }
});
/** How many SKUs an axis pair creates: a `none` axis counts as one. */
export const countBaseGarmentCombinations = (sizes, colours) => {
    const axis = (a) => a.none ? 1 : a.codes.length + a.newCodes.length;
    return axis(sizes) * axis(colours);
};
export const addBaseGarmentVariantsSchema = z
    .object({
    locationId: uuidSchema,
    code: baseGarmentCodeSchema,
    sizes: baseGarmentAxisSchema,
    colours: baseGarmentAxisSchema,
})
    .superRefine((value, context) => {
    if (countBaseGarmentCombinations(value.sizes, value.colours) > BASE_GARMENT_MAX_VARIANTS) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Create at most ${BASE_GARMENT_MAX_VARIANTS} SKUs at a time`,
            path: ['colours'],
        });
    }
});
const hsCodeSchema = z
    .string()
    .trim()
    .max(BASE_GARMENT_HS_CODE_MAX)
    .nullable()
    .transform((value) => (value === '' ? null : value));
export const baseGarmentCodePatchSchema = z
    .object({
    name: baseGarmentNameSchema.optional(),
    newCode: newProductCodeSchema.optional(),
    audience: baseGarmentAudienceSchema.optional(),
    clientOrganizationId: uuidSchema.nullable().optional(),
    defaultPrintMethod: baseGarmentPrintMethodSchema.optional(),
    lowStockAlert: z.number().int().min(0).max(BASE_GARMENT_STOCK_MAX).nullable().optional(),
    supplierId: uuidSchema.nullable().optional(),
    hsCode: hsCodeSchema.optional(),
})
    .superRefine((value, context) => {
    if (Object.values(value).every((entry) => entry === undefined)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'There is nothing to change' });
        return;
    }
    if (value.audience !== undefined) {
        refineAudienceClient({ audience: value.audience, clientOrganizationId: value.clientOrganizationId }, context);
    }
    else if (value.clientOrganizationId !== undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Change the client together with the stock type',
            path: ['clientOrganizationId'],
        });
    }
});
export const updateBaseGarmentCodeSchema = z.object({
    code: baseGarmentCodeSchema,
    /** Required for lowStockAlert (the alert is stored per location). */
    locationId: uuidSchema.optional(),
    patch: baseGarmentCodePatchSchema,
});
/**
 * BLU-1436: the stock type of a pick-and-pack product code, set from the Products PAP
 * tab: `own` (Own stock), `all_clients` (Shared) or `client` (Labelled for that client).
 * Every live PAP SKU starting `<code>` (or equal to it) takes it. No organizationId: the
 * org comes from auth. Wording follows the "Stock type" rule (BLU-1443).
 */
export const setPapCodeAudienceSchema = z
    .object({
    code: baseGarmentCodeSchema,
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: uuidSchema.optional(),
})
    .superRefine((value, context) => refineAudienceClient(value, context, [], {
    missing: 'Choose the client this stock is labelled for',
    extra: 'A client only goes with Labelled for a client',
}));
export const updateBaseGarmentGridSchema = z
    .object({
    code: baseGarmentCodeSchema,
    /** Grams per size; size null = a size-less code; grams null clears. */
    weights: z
        .array(z.object({
        size: nullableSegmentSchema,
        grams: z.number().finite().positive().max(BASE_GARMENT_WEIGHT_MAX_GRAMS).nullable(),
    }))
        .max(BASE_GARMENT_MAX_GRID_ENTRIES)
        .optional(),
    /**
     * Cost per size, or per size and colour; amount null clears. Needs the cost
     * permission. `currency` is the amount's currency: absent = USD (the grid's
     * column), null = the org currency (a cost saved that way keeps it), else
     * one of the supported codes.
     */
    costs: z
        .array(z.object({
        size: nullableSegmentSchema,
        colour: nullableSegmentSchema,
        amount: clientPriceAmountSchema.nullable(),
        currency: costCurrencySchema.nullable().optional(),
    }))
        .max(BASE_GARMENT_MAX_GRID_ENTRIES)
        .optional(),
    /**
     * One image per colour, applied to every size of that colour; url null
     * clears. The shape here; the backend applies `isAcceptedBaseGarmentImageUrl`.
     */
    images: z
        .array(z.object({
        colour: segmentSchema,
        url: z
            .string()
            .trim()
            .max(2048)
            .regex(BASE_GARMENT_GRID_IMAGE_URL_RE, 'Use an https:// web address')
            .nullable(),
    }))
        .max(BASE_GARMENT_MAX_GRID_ENTRIES)
        .optional(),
})
    .superRefine((value, context) => {
    const total = (value.weights?.length ?? 0) + (value.costs?.length ?? 0) + (value.images?.length ?? 0);
    if (total === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'There is nothing to save', path: ['weights'] });
    }
});
export const adjustBaseGarmentStockSchema = z
    .object({
    locationId: uuidSchema,
    reason: baseGarmentStockReasonSchema,
    note: z.string().trim().max(BASE_GARMENT_NOTE_MAX).optional(),
    lines: z
        .array(z.object({ skuId: uuidSchema, quantity: z.number().int().min(0).max(BASE_GARMENT_STOCK_MAX) }))
        .min(1)
        .max(BASE_GARMENT_MAX_STOCK_LINES),
})
    .superRefine((value, context) => {
    if (new Set(value.lines.map((line) => line.skuId)).size !== value.lines.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'A SKU is listed twice', path: ['lines'] });
    }
});
export const copyBaseGarmentCodeSchema = z
    .object({
    locationId: uuidSchema,
    sourceCode: baseGarmentCodeSchema,
    newCode: newProductCodeSchema,
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: uuidSchema.optional(),
})
    .superRefine((value, context) => refineAudienceClient(value, context));
/** One CSV row as the import RPC reads it (camelCase keys; a missing key = leave as is). */
export const baseGarmentImportRowSchema = z.object({
    code: baseGarmentCodeSchema,
    size: sizeColourCodeSchema.nullable(),
    colour: sizeColourCodeSchema,
    sizeName: segmentNameSchema.nullable().optional(),
    colourName: segmentNameSchema.nullable().optional(),
    name: baseGarmentNameSchema.optional(),
    audience: baseGarmentAudienceSchema.optional(),
    clientOrganizationId: uuidSchema.nullable().optional(),
    printMethod: baseGarmentPrintMethodSchema.optional(),
    lowStockAlert: z.number().int().min(0).max(BASE_GARMENT_STOCK_MAX).nullable().optional(),
    supplierId: uuidSchema.nullable().optional(),
    hsCode: hsCodeSchema.optional(),
    weightG: z.number().finite().positive().max(BASE_GARMENT_WEIGHT_MAX_GRAMS).nullable().optional(),
    imageUrl: z.string().trim().max(2048).regex(BASE_GARMENT_IMAGE_URL_RE).nullable().optional(),
    costUsd: clientPriceAmountSchema.nullable().optional(),
    stock: z.number().int().min(0).max(BASE_GARMENT_STOCK_MAX).nullable().optional(),
});
export const baseGarmentNewCodeAudienceSchema = z
    .object({
    code: newProductCodeSchema,
    name: baseGarmentNameSchema,
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: uuidSchema.optional(),
})
    .superRefine((value, context) => refineAudienceClient(value, context));
export const importBaseGarmentsSchema = z.object({
    /** Browser-generated; a retry with the same id returns the first attempt's result. */
    importId: uuidSchema,
    locationId: uuidSchema,
    filename: z.string().trim().min(1).max(255),
    rows: z.array(baseGarmentImportRowSchema).min(1).max(BASE_GARMENT_MAX_IMPORT_ROWS),
    newCodeAudiences: z.array(baseGarmentNewCodeAudienceSchema).max(BASE_GARMENT_MAX_IMPORT_NEW_CODES).default([]),
});
// ---------------------------------------------------------------------------
// BLU-1527 S6: the "Substitute garment" of a main garment code (an own
// substitute: one of the org's own garments its orders may use when the main
// garment is out). The browser sends the substitute code and its manual
// choices only; the backend runs the matcher (substitute-garment-match.ts)
// and writes what it picks.
// ---------------------------------------------------------------------------
/** The most manual choices one save carries (one per main blank). */
export const OWN_SUBSTITUTE_MAX_OVERRIDES = 2000;
/** One manual choice: this main blank uses that substitute blank (null = leave it out). */
export const ownSubstituteOverrideSchema = z.object({
    mainSkuId: uuidSchema,
    subSkuId: uuidSchema.nullable(),
});
const ownSubstituteOverridesSchema = z
    .array(ownSubstituteOverrideSchema)
    .max(OWN_SUBSTITUTE_MAX_OVERRIDES)
    .refine((list) => new Set(list.map((entry) => entry.mainSkuId)).size === list.length, {
    message: 'Each main garment can have one choice',
})
    .default([]);
/** The editor read: the main code, and optionally a substitute code to preview (default: the saved one). */
export const ownSubstituteQuerySchema = z.object({
    code: baseGarmentCodeSchema,
    subCode: baseGarmentCodeSchema.nullable().optional(),
});
/** The match a save would write, with the manual choices applied (writes nothing). */
export const previewOwnSubstituteSchema = z
    .object({
    code: baseGarmentCodeSchema,
    subCode: baseGarmentCodeSchema,
    overrides: ownSubstituteOverridesSchema,
})
    .refine((value) => value.code !== value.subCode, {
    message: 'A garment cannot be its own substitute',
    path: ['subCode'],
});
/** Save the substitute (subCode null clears it, like clearOwnSubstitute). */
export const setOwnSubstituteSchema = z
    .object({
    code: baseGarmentCodeSchema,
    subCode: baseGarmentCodeSchema.nullable(),
    overrides: ownSubstituteOverridesSchema,
})
    .refine((value) => value.code !== value.subCode, {
    message: 'A garment cannot be its own substitute',
    path: ['subCode'],
});
/** Remove the substitute: every pair of the main code goes. */
export const clearOwnSubstituteSchema = z.object({ code: baseGarmentCodeSchema });
